/**
 * Workflow Action Buttons Component
 * Displays workflow action buttons based on current verification status and user permissions
 *
 * Workflow States:
 * - TODO: Can be Submitted for QA (by editors)
 * - READY_FOR_QA: Can be Approved or Rejected (by approvers)
 * - APPROVED: Can be Reopened for editing (by approvers)
 * - REJECTED: Can be Resubmitted (by editors)
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import { useGlobalConfig } from 'context/GlobalContext';
import { ANALYTICS_EVENTS } from 'framework/analytics/AnalyticsEvents';
import { ConfigRegistryApis } from '../api/configRegistryAPI';
import { ConfigRegistryItem } from '../types/configRegistryTypes';

interface WorkflowActionButtonsProps {
  config: ConfigRegistryItem;
  isDirty?: boolean;
  onSaveFirst?: () => Promise<void>;
  onActionComplete: (updatedConfig: ConfigRegistryItem) => Promise<void>;
  onError: (message: string) => void;
}

type ActionType = 'approve' | 'reject' | 'resubmit' | 'reopen' | 'submit' | null;

export const WorkflowActionButtons = ({
  config,
  isDirty = false,
  onSaveFirst,
  onActionComplete,
  onError,
}: WorkflowActionButtonsProps) => {
  const { rc } = useGlobalConfig();
  const [isLoading, setIsLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ActionType>(null);

  const canApprove = rc.permissionsManager.canConfigRegistryApprove();
  const canEdit = rc.permissionsManager.canConfigRegistryEdit();
  const verificationStatus = config.verificationStatus;

  // Determine which actions are available based on status and permissions
  const showSubmit = (verificationStatus === 'TODO' || verificationStatus === 'REJECTED') && canEdit;
  const showApprove = verificationStatus === 'READY_FOR_QA' && canApprove;
  const showReject = verificationStatus === 'READY_FOR_QA' && canApprove;
  const showReopen = verificationStatus === 'APPROVED' && canApprove;

  // Publish-style workflow actions (submit/resubmit for review, approve).
  const isPublishAction = (action: ActionType) =>
    action === 'submit' || action === 'resubmit' || action === 'approve';

  const handleAction = async (action: ActionType) => {
    if (!action) return;

    // Analytics: publish/approval intent confirmed.
    if (isPublishAction(action)) {
      rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_REGISTRY.PUBLISH_CLICKED, {
        config_type: config.configType,
      });
    }

    // Only auto-save before "submit" action
    // Approve/Reject/Reopen actions don't need the form to be saved first
    if (action === 'submit' && isDirty && onSaveFirst) {
      try {
        await onSaveFirst();
      } catch (err: any) {
        rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_REGISTRY.PUBLISH_FAILED, {
          error_type: 'api_error',
          severity: 'critical',
        });
        onError(err.message || 'Failed to save changes before submitting');
        setConfirmAction(null);
        return;
      }
    }

    setIsLoading(true);
    try {
      let response;
      switch (action) {
        case 'submit':
          response = await ConfigRegistryApis.submitForApproval(rc, config.documentId);
          break;
        case 'approve':
          response = await ConfigRegistryApis.approveConfig(rc, config.documentId);
          break;
        case 'reject':
          response = await ConfigRegistryApis.rejectConfig(rc, config.documentId);
          break;
        case 'resubmit':
          response = await ConfigRegistryApis.submitForApproval(rc, config.documentId);
          break;
        case 'reopen':
          response = await ConfigRegistryApis.reopenConfig(rc, config.documentId);
          break;
      }

      if (response?.error) {
        if (isPublishAction(action)) {
          rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_REGISTRY.PUBLISH_FAILED, {
            error_type: 'api_error',
            severity: 'critical',
          });
        }
        onError(response.error.message || `Failed to ${action} config`);
      } else if (response?.data) {
        // Extract the config data - handle both response.data.data and response.data
        const configData = (response.data as any).data || response.data;

        // Analytics: publish/approval succeeded.
        if (isPublishAction(action)) {
          rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_REGISTRY.PUBLISHED, {
            config_type: config.configType,
          });
        }

        // Await the callback to ensure refetch completes before resetting UI state
        await onActionComplete(configData);
      } else {
        if (isPublishAction(action)) {
          rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_REGISTRY.PUBLISH_FAILED, {
            error_type: 'api_error',
            severity: 'critical',
          });
        }
        onError(`Unexpected response structure from ${action} action`);
      }
    } catch (err: any) {
      if (isPublishAction(action)) {
        rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_REGISTRY.PUBLISH_FAILED, {
          error_type: 'api_error',
          severity: 'critical',
        });
      }
      onError(err.message || `Failed to ${action} config`);
    } finally {
      setIsLoading(false);
      setConfirmAction(null);
    }
  };

  const getConfirmDialogContent = () => {
    switch (confirmAction) {
      case 'submit':
        return {
          title: 'Submit for Review',
          content: isDirty
            ? `Your changes will be saved and "${config.displayName}" will be submitted for review.`
            : `Are you sure you want to submit "${config.displayName}" for review?`,
          confirmText: isDirty ? 'Save & Submit' : 'Submit',
          confirmColor: 'primary' as const,
        };
      case 'approve':
        return {
          title: 'Approve Configuration',
          content: `Are you sure you want to approve "${config.displayName}"?`,
          confirmText: 'Approve',
          confirmColor: 'success' as const,
        };
      case 'reject':
        return {
          title: 'Reject Configuration',
          content: `Are you sure you want to reject "${config.displayName}"? This will require the editor to make changes and resubmit.`,
          confirmText: 'Reject',
          confirmColor: 'error' as const,
        };
      case 'resubmit':
        return {
          title: 'Resubmit for Review',
          content: `Are you sure you want to resubmit "${config.displayName}" for review?`,
          confirmText: 'Resubmit',
          confirmColor: 'primary' as const,
        };
      case 'reopen':
        return {
          title: 'Reopen for Editing',
          content: `Are you sure you want to reopen "${config.displayName}" for editing? This will change its status back to "TODO".`,
          confirmText: 'Reopen',
          confirmColor: 'warning' as const,
        };
      default:
        return null;
    }
  };

  const dialogContent = getConfirmDialogContent();

  // Don't render if no actions available
  if (!showSubmit && !showApprove && !showReject && !showReopen) {
    return null;
  }

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {showSubmit && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<SendIcon />}
            onClick={() => setConfirmAction('submit')}
            disabled={isLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {isDirty ? 'Save & Submit for Review' : 'Submit for Review'}
          </Button>
        )}
        {showApprove && (
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<CheckCircleIcon />}
            onClick={() => setConfirmAction('approve')}
            disabled={isLoading}
          >
            Approve
          </Button>
        )}
        {showReject && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<CancelIcon />}
            onClick={() => setConfirmAction('reject')}
            disabled={isLoading}
          >
            Reject
          </Button>
        )}
        {showReopen && (
          <Button
            variant="outlined"
            color="warning"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => setConfirmAction('reopen')}
            disabled={isLoading}
          >
            Reopen for Editing
          </Button>
        )}
      </Box>

      {/* Confirmation Dialog */}
      {dialogContent && (
        <Dialog
          open={confirmAction !== null}
          onClose={() => setConfirmAction(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{dialogContent.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {dialogContent.content}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setConfirmAction(null)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleAction(confirmAction)}
              color={dialogContent.confirmColor}
              variant="contained"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
            >
              {isLoading ? 'Processing...' : dialogContent.confirmText}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default WorkflowActionButtons;

